import { useEffect, useState } from "react";
import { School, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AGE_GROUPS, label } from "@/lib/activityData";

const EMPTY = {
  teacher_name: "", classroom_name: "", program_site: "", age_group: "",
  num_children: "", primary_language: "", languages_spoken: "", curriculum: "",
  current_theme: "", instructional_goals: "",
};

export default function ClassroomProfile() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => { if (data) setForm({ ...EMPTY, ...data }); })
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async () => {
    setSaving(true);
    try {
      const { teacher_name, classroom_name, program_site, age_group, num_children,
        primary_language, languages_spoken, curriculum, current_theme, instructional_goals } = form;
      await api.put("/profile", { teacher_name, classroom_name, program_site, age_group, num_children, primary_language, languages_spoken, curriculum, current_theme, instructional_goals });
      toast.success(t("common.saved"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (loading) {
    return (<div className="min-h-screen bg-background"><Header /><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></div>);
  }

  const field = (key, testid, textarea = false) => (
    <div className="space-y-2">
      <Label>{t(`profile.${key}`)}</Label>
      {textarea
        ? <Textarea rows={3} value={form[keyMap[key]]} onChange={set(keyMap[key])} className="rounded-xl resize-none" data-testid={testid} />
        : <Input value={form[keyMap[key]]} onChange={set(keyMap[key])} className="h-12 rounded-xl" data-testid={testid} />}
    </div>
  );

  const keyMap = {
    teacherName: "teacher_name", classroomName: "classroom_name", program: "program_site",
    numChildren: "num_children", primaryLanguage: "primary_language", languagesSpoken: "languages_spoken",
    curriculum: "curriculum", currentTheme: "current_theme", goals: "instructional_goals",
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-2xl bg-accent/15 flex items-center justify-center"><School className="h-6 w-6 text-accent" /></span>
          <div>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight">{t("profile.title")}</h1>
          </div>
        </div>
        <p className="text-muted-foreground">{t("profile.subtitle")}</p>

        <div className="space-y-5 rounded-3xl border border-border bg-card p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-5">
            {field("teacherName", "prof-teacher-name")}
            {field("classroomName", "prof-classroom-name")}
            {field("program", "prof-program")}
            <div className="space-y-2">
              <Label>{t("profile.ageGroup")}</Label>
              <Select value={form.age_group} onValueChange={set("age_group")}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="prof-age"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {AGE_GROUPS.map((a) => <SelectItem key={a.value} value={a.value}>{label(a, lang)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {field("numChildren", "prof-num-children")}
            {field("primaryLanguage", "prof-primary-language")}
            {field("languagesSpoken", "prof-languages-spoken")}
            {field("curriculum", "prof-curriculum")}
            {field("currentTheme", "prof-current-theme")}
          </div>
          {field("goals", "prof-goals", true)}
          <Button onClick={save} disabled={saving} className="rounded-xl bg-secondary hover:bg-secondary/90 h-12 px-8" data-testid="prof-save">
            {saving ? t("common.loading") : t("common.save")}
          </Button>
        </div>
      </main>
    </div>
  );
}
